// Celebration Book occasion types, cover metadata, and curated prompt packs

export const OCCASION_TYPES = [
  { id: 'MOTHERS_DAY', label: "Mother's Day", emoji: '💐', accent: '#d4a0a0', defaultTitle: (name) => `For ${name}, With Love` },
  { id: 'FATHERS_DAY', label: "Father's Day", emoji: '🏔️', accent: '#3d5a80', defaultTitle: (name) => `For ${name}, With Love` },
  { id: 'BIRTHDAY', label: 'Birthday', emoji: '🎂', accent: '#c4933f', defaultTitle: (name) => `Happy Birthday, ${name}` },
  { id: 'GRANDPARENTS_DAY', label: "Grandparents Day", emoji: '🌳', accent: '#6b8f71', defaultTitle: (name) => `For ${name}, From All of Us` },
  { id: 'CHRISTMAS', label: 'Christmas', emoji: '🎄', accent: '#8b2e2e', defaultTitle: (name) => `A Christmas Gift for ${name}` },
  { id: 'ANNIVERSARY', label: 'Anniversary', emoji: '💍', accent: '#b04a6a', defaultTitle: (name) => `For ${name}, With Love` },
  { id: 'WEDDING', label: 'Wedding', emoji: '💒', accent: '#c9a84c', defaultTitle: (name) => `For ${name}, On Your Wedding Day` },
  { id: 'IN_MEMORY', label: 'In Memory', emoji: '🕊️', accent: '#6b7a8f', defaultTitle: (name) => `Remembering ${name}` },
  { id: 'RETIREMENT', label: 'Retirement', emoji: '🎉', accent: '#3d7a6b', defaultTitle: (name) => `Cheers to You, ${name}` },
  { id: 'GRADUATION', label: 'Graduation', emoji: '🎓', accent: '#3d5a80', defaultTitle: (name) => `Congratulations, ${name}` },
  { id: 'VALENTINES', label: "Valentine's Day", emoji: '💗', accent: '#c45c7c', defaultTitle: (name) => `For ${name}, With Love` },
  { id: 'NEW_BABY', label: 'New Baby', emoji: '👶', accent: '#8fb0a0', defaultTitle: (name) => `Welcome, ${name}` },
  { id: 'APPRECIATION', label: 'Just Because', emoji: '💛', accent: '#c4933f', defaultTitle: (name) => `For ${name}, Just Because` },
  { id: 'OTHER', label: 'Other', emoji: '🎁', accent: '#3c4653', defaultTitle: (name) => `A Book for ${name}` },
];

export const CELEBRATION_PROMPTS = {
  MOTHERS_DAY: [
    { emoji: '💛', text: "What's the first memory of your mom that comes to mind right now? Just the first one." },
    { emoji: '😄', text: "What could always make your mom laugh, the real laugh, not the polite one?" },
    { emoji: '👃', text: "What's a smell, a sound, or a taste that takes you straight back to her?" },
    { emoji: '🍳', text: "Tell me about something she made or built or cooked that she was quietly proud of." },
    { emoji: '📞', text: "What's something she always said that you can still hear in her voice today?" },
    { emoji: '👀', text: "Tell me about a moment you watched her doing something she didn't think you noticed." },
    { emoji: '💪', text: "What's the hardest thing you watched her go through? Tell me what you actually saw, not what you thought she felt." },
    { emoji: '🚪', text: "Tell me about a time she surprised you. Not necessarily pleasantly, just a moment you didn't see coming." },
    { emoji: '🌫️', text: "What did you not understand about your mom when you were young that you understand differently now?" },
    { emoji: '🪞', text: "What part of yourself did you recognize as hers, something you noticed and thought, that came from her?" },
    { emoji: '✨', text: "What's a side of her that most people didn't get to see?" },
    { emoji: '🏆', text: "What's a moment you felt proud of her, not of something she did for you, but proud of who she was?" },
    { emoji: '🌸', text: "What do you wish she knew about the mark she left on you?" },
    { emoji: '💌', text: "What would you want to tell her if you could, or tell her more often if you still can?" },
    { emoji: '🌅', text: "If you could give her one afternoon, any age, any place, what would you put in it?" },
  ],

  MOTHERS_DAY_PARTNER: [
    { emoji: '✨', text: "When did you first see it? The moment you knew she was going to be a great mom." },
    { emoji: '💛', text: "What does she do as a mom that you quietly marvel at, something she probably doesn't notice you noticing?" },
    { emoji: '😄', text: "What's something she always does with the kids that is so entirely her, you'd know it from another room?" },
    { emoji: '👶', text: "Tell me about a time you watched her with the kids when she didn't know you were watching." },
    { emoji: '🌙', text: "What's something she does late at night, or early in the morning, for the kids that nobody sees but you?" },
    { emoji: '💪', text: "What's the hardest thing you've watched her handle as a mom? Tell me what that looked like from where you stood." },
    { emoji: '🧩', text: "What did you not understand about what she was carrying until you were both in the middle of it?" },
    { emoji: '☀️', text: "What's a moment you saw her truly at ease, not just holding it together, but actually at ease, as a mom?" },
    { emoji: '🌱', text: "What's the most important thing you've watched her pass to the kids, not by talking about it but by how she lives?" },
    { emoji: '🌟', text: "What's a moment you felt proud of her as a mom, not of the outcome, but of her?" },
    { emoji: '🤍', text: "What do you hope the kids carry from her someday, something they might not even know they got from her?" },
    { emoji: '🔄', text: "What has watching her be a mom changed in you?" },
    { emoji: '💌', text: "What do you wish she knew about how much she means to this family, the part she might not see from inside it?" },
    { emoji: '🌅', text: "If you could give her one perfect day as a mom, just for her, no logistics, no to-do list, what would it look like?" },
  ],

  FATHERS_DAY: [
    { emoji: '💛', text: "What's an early memory of your dad, the first one that surfaces when you think of him?" },
    { emoji: '🛠️', text: "Tell me about something your dad did with his hands, something you watched him fix, make, build, or tend to." },
    { emoji: '🗣️', text: "What's something your dad said, or a way he said things, that you could still do an impression of right now?" },
    { emoji: '🚗', text: "Tell me about a trip, a drive, or a morning errand you remember taking with him." },
    { emoji: '🌄', text: "What was something he loved, a place, a pastime, a team, something entirely his, that had nothing to do with being a dad?" },
    { emoji: '📺', text: "What's a show, a sport, a song, or a habit you associate only with him?" },
    { emoji: '👁️', text: "Tell me about a time you watched your dad with someone else, a friend, a stranger, someone at work." },
    { emoji: '⚡', text: "What's a moment you saw your dad rattled, or uncertain, or out of his depth?" },
    { emoji: '🌫️', text: "What did you not understand about your dad when you were young that lands differently now?" },
    { emoji: '🪞', text: "What part of yourself did you recognize as his, something that surprised you when you noticed it showing up?" },
    { emoji: '✨', text: "What's something about your dad most people wouldn't guess from the outside?" },
    { emoji: '🏔️', text: "What's a moment you felt proud of him, something where you thought, yes, that's him?" },
    { emoji: '🌱', text: "What's the most important thing you got from him, something you carry now, even if you didn't ask for it?" },
    { emoji: '💌', text: "What would you want him to know, or want to tell him more often, if you still have the chance?" },
    { emoji: '🌅', text: "If you could give your dad one afternoon, anywhere, what would you put in it?" },
  ],

  BIRTHDAY: [
    { emoji: '🎉', text: "What's your favorite memory with the birthday person?" },
    { emoji: '😂', text: "What's the funniest moment you've shared together?" },
    { emoji: '💛', text: "What do you love most about them?" },
    { emoji: '✨', text: "Describe them in three words and tell a story for each." },
    { emoji: '🌟', text: "What makes them one of a kind?" },
    { emoji: '🤗', text: "Describe a time they showed up for you when it mattered." },
    { emoji: '🗣️', text: "What's the best advice they've ever given you?" },
    { emoji: '🎁', text: "What's a gift — material or not — they've given you that you'll never forget?" },
    { emoji: '💌', text: "If you could tell them one thing they might not know, what would it be?" },
    { emoji: '👶', text: "What's your earliest memory of them?" },
    { emoji: '🏆', text: "What are you most proud of them for?" },
    { emoji: '🗺️', text: "Describe a trip, adventure, or wild night you shared." },
    { emoji: '🍰', text: "What's your favorite birthday memory involving them?" },
    { emoji: '🔮', text: "What do you wish for them in the year ahead?" },
    { emoji: '🤝', text: "How has knowing them changed your life?" },
  ],

  GRANDPARENTS_DAY: [
    { emoji: '💛', text: "What's your favorite memory with Grandma or Grandpa?" },
    { emoji: '🍪', text: "What food, recipe, or smell reminds you of their house?" },
    { emoji: '📖', text: "What story have they told that you never get tired of hearing?" },
    { emoji: '🌳', text: "What family tradition started with them?" },
    { emoji: '🤗', text: "Describe what it felt like to visit their house as a kid." },
    { emoji: '🗣️', text: "What's the wisest thing they ever said to you?" },
    { emoji: '😂', text: "What's the funniest thing you remember about them?" },
    { emoji: '💪', text: "What did they live through that amazes you?" },
    { emoji: '🧸', text: "What's your earliest memory of them?" },
    { emoji: '🎵', text: "Is there a song, sound, or place that instantly brings them to mind?" },
    { emoji: '✨', text: "What quality of theirs do you hope to carry forward?" },
    { emoji: '💌', text: "What would you want them to know about how they shaped your life?" },
    { emoji: '🏡', text: "What did their love look like in small, everyday moments?" },
    { emoji: '👨‍👩‍👧‍👦', text: "How did they bring the family together?" },
    { emoji: '🌅', text: "What lesson from their generation do you think your generation needs?" },
  ],

  CHRISTMAS: [
    { emoji: '🎄', text: "What's your favorite Christmas memory together?" },
    { emoji: '🎁', text: "What's the most meaningful gift you've given or received from them?" },
    { emoji: '🍽️', text: "What Christmas tradition or meal wouldn't be the same without them?" },
    { emoji: '😂', text: "What's the funniest thing that happened at a holiday gathering?" },
    { emoji: '❄️', text: "Describe a Christmas morning from childhood." },
    { emoji: '🕯️', text: "What does the holiday season mean to your family?" },
    { emoji: '🎶', text: "Is there a Christmas song, movie, or moment that reminds you of them?" },
    { emoji: '💛', text: "What do you love most about spending the holidays with this person?" },
    { emoji: '🏠', text: "Describe what the house felt like during the holidays growing up." },
    { emoji: '⭐', text: "What holiday tradition do you want to keep alive for the next generation?" },
    { emoji: '🤗', text: "What small thing do they do during the holidays that means the world?" },
    { emoji: '💌', text: "If you wrote them a Christmas letter from the heart, what would it say?" },
    { emoji: '🧣', text: "Describe a time the holidays didn't go as planned — but turned out great." },
    { emoji: '🌟', text: "What makes this person the heart of your holiday gatherings?" },
    { emoji: '🎅', text: "What's a holiday tradition you started because of them?" },
  ],

  ANNIVERSARY: [
    { emoji: '💛', text: "How did the two of you first meet? Take me back to that moment." },
    { emoji: '✨', text: "When did you know this was the person you wanted to spend your life with?" },
    { emoji: '😂', text: "What's a story from your early days together that still makes you laugh?" },
    { emoji: '💍', text: "Tell me about the day you got married, or the day you committed to each other." },
    { emoji: '🏠', text: "What's a home, a trip, or a season of life the two of you built together that you're proud of?" },
    { emoji: '🤝', text: "What's something you've learned about love from being with them all these years?" },
    { emoji: '💪', text: "Tell me about a hard time you weathered together, and how you got through it side by side." },
    { emoji: '☀️', text: "What's a small, ordinary thing they do that you've quietly loved for years?" },
    { emoji: '🌟', text: "What do they do better than anyone, and how have you watched them grow?" },
    { emoji: '🤗', text: "When have you felt most cared for by them?" },
    { emoji: '🎵', text: "Is there a song, a place, or a tradition that belongs just to the two of you?" },
    { emoji: '🌅', text: "What are you looking forward to in the years still ahead of you together?" },
    { emoji: '💌', text: "If you could say one thing to them on this anniversary, what would it be?" },
  ],

  WEDDING: [
    { emoji: '💛', text: "What's your favorite memory with the person, or the couple, you're celebrating?" },
    { emoji: '✨', text: "What did you think the first time you saw them together?" },
    { emoji: '😂', text: "What's a story about them that everyone at the wedding would love to hear?" },
    { emoji: '🤗', text: "Tell me about a moment you watched them be there for someone they love." },
    { emoji: '🌟', text: "What do you admire most about who they are, and who they're becoming together?" },
    { emoji: '🏠', text: "What does the kind of love you hope for them actually look like, day to day?" },
    { emoji: '🗣️', text: "What's the best piece of advice you'd give them about love or marriage?" },
    { emoji: '💪', text: "What's something you know about staying together through the hard parts?" },
    { emoji: '🎵', text: "Is there a song, a tradition, or a moment you can already picture from their life together?" },
    { emoji: '🌱', text: "What do you hope they never forget, even decades from now?" },
    { emoji: '🍰', text: "What are you most looking forward to watching them do as a married couple?" },
    { emoji: '💌', text: "If you could give them one wish for their marriage, what would it be?" },
    { emoji: '🌅', text: "What do you want them to know about how loved they are as they start this chapter?" },
  ],

  IN_MEMORY: [
    { emoji: '🕊️', text: "Whenever you're ready, tell me who they were to you." },
    { emoji: '💛', text: "What's a memory of them that comes back on its own, the one that just arrives?" },
    { emoji: '👁️', text: "Picture them doing something completely ordinary. What are they doing?" },
    { emoji: '👂', text: "Is there something they said so often you could still hear it now?" },
    { emoji: '😄', text: "What made them laugh? Not a polite smile, actually laugh." },
    { emoji: '🪑', text: "What place or spot do you most associate with them?" },
    { emoji: '🤝', text: "Tell me about a moment you watched them with someone else. What did you see?" },
    { emoji: '✋', text: "What's something they did or made with their hands that you remember clearly?" },
    { emoji: '🌅', text: "When do you remember them being simply content, not performing happiness, just at ease?" },
    { emoji: '🌱', text: "What's something you carry from them, a way of doing something, a phrase, a habit, something that showed up in you without you trying?" },
    { emoji: '💬', text: "Is there something you never quite got to say to them? You don't have to share it, but if you want to, this is the place." },
    { emoji: '🔍', text: "What did most people not see in them, something only you or a few others knew?" },
    { emoji: '📸', text: "If you could point to one moment with them and say, that's them, that's who they really were, what would it be?" },
    { emoji: '🌸', text: "What do you want people to remember about them when they think of them years from now?" },
  ],

  RETIREMENT: [
    { emoji: '🎉', text: "What's your favorite memory of working alongside them, or watching them work?" },
    { emoji: '🌟', text: "What were they known for? The thing colleagues would always come to them for." },
    { emoji: '😂', text: "What's a story from over the years that still makes you smile?" },
    { emoji: '🤝', text: "Tell me about a time they went out of their way for someone at work." },
    { emoji: '💪', text: "What's a challenge you watched them take on and handle their own way?" },
    { emoji: '🗣️', text: "What's the best thing you ever learned from them?" },
    { emoji: '🏆', text: "What accomplishment of theirs are you most proud to have witnessed?" },
    { emoji: '☀️', text: "What did they bring to the team beyond the work itself?" },
    { emoji: '🌱', text: "How did they make the people around them better?" },
    { emoji: '✨', text: "What do you want them to know about the mark they're leaving behind?" },
    { emoji: '🌅', text: "What do you hope this next chapter holds for them?" },
    { emoji: '💌', text: "If you could thank them for one thing, what would it be?" },
  ],

  GRADUATION: [
    { emoji: '🎓', text: "What's a moment from these years that you'll always remember about them?" },
    { emoji: '🌱', text: "How have you watched them grow into the person they are today?" },
    { emoji: '😂', text: "What's a story from along the way that still makes you laugh?" },
    { emoji: '💪', text: "Tell me about a time they worked hard for something, or pushed through when it was tough." },
    { emoji: '🌟', text: "What's a strength of theirs that you hope they always hold onto?" },
    { emoji: '🏆', text: "What's the moment you felt most proud of them?" },
    { emoji: '🤗', text: "When did you realize how much they'd grown up?" },
    { emoji: '🗣️', text: "What's a piece of advice you want them to carry into what's next?" },
    { emoji: '☀️', text: "What do you love most about who they're becoming?" },
    { emoji: '🌅', text: "What do you hope the road ahead holds for them?" },
    { emoji: '✨', text: "What do you believe they're capable of, even if they don't see it yet?" },
    { emoji: '💌', text: "If you could tell them one thing on this day, what would it be?" },
  ],

  VALENTINES: [
    { emoji: '💗', text: "What do you love most about them? The first thing that comes to mind." },
    { emoji: '💛', text: "When did you first realize you were falling for them?" },
    { emoji: '😊', text: "What's a small, everyday moment with them that means more to you than they probably know?" },
    { emoji: '😂', text: "What's something they do that makes you laugh every time?" },
    { emoji: '🗺️', text: "What's an adventure or a night together you'd happily relive?" },
    { emoji: '🤗', text: "When have you felt most loved by them?" },
    { emoji: '✨', text: "What's a side of them that only you get to see?" },
    { emoji: '💪', text: "When did they show up for you in a way you'll never forget?" },
    { emoji: '🎵', text: "Is there a song, a smell, or a place that instantly brings them to mind?" },
    { emoji: '🌟', text: "What do they make possible in your life that wouldn't be there without them?" },
    { emoji: '🌅', text: "What's something you're looking forward to doing together?" },
    { emoji: '💌', text: "If you could put one feeling about them into words, what would it be?" },
  ],

  NEW_BABY: [
    { emoji: '👶', text: "Tell me about the day they arrived. What do you remember most?" },
    { emoji: '💛', text: "What was the very first thing you felt when you met them?" },
    { emoji: '✨', text: "How did you choose their name, and what does it mean to you?" },
    { emoji: '🏠', text: "What's the family they're being welcomed into, and who can't wait to meet them?" },
    { emoji: '🌱', text: "What hopes do you already hold for the kind of person they'll grow into?" },
    { emoji: '🌟', text: "What do you want them to know about how long they were wished for?" },
    { emoji: '🤗', text: "What's a moment in these early days you never want to forget?" },
    { emoji: '☀️', text: "What do you most look forward to sharing with them as they grow?" },
    { emoji: '🗣️', text: "What's something you hope to teach them, or a value you want to pass on?" },
    { emoji: '🎵', text: "Is there a song, a story, or a tradition you can't wait to give them?" },
    { emoji: '🌅', text: "What kind of world do you hope they find waiting for them?" },
    { emoji: '💌', text: "If you could write them a note to read when they're grown, what would it say?" },
  ],

  APPRECIATION: [
    { emoji: '💛', text: "What's the first thing you'd want them to know about what they mean to you?" },
    { emoji: '😊', text: "What's a small, everyday thing they do that you appreciate more than they realize?" },
    { emoji: '✨', text: "When did you realize how lucky you were to have them in your life?" },
    { emoji: '🤗', text: "Tell me about a time they showed up for you when you needed it most." },
    { emoji: '🌟', text: "What do you admire most about who they are?" },
    { emoji: '💕', text: "What's a moment with them you'd never want to forget?" },
    { emoji: '🔎', text: "What makes them them, the little things only someone close would notice?" },
    { emoji: '☀️', text: "What do you love about the ordinary, everyday moments with them?" },
    { emoji: '🫶', text: "What's a way they make you feel that you wish you could put into words?" },
    { emoji: '🗣️', text: "What's something you've never quite found the moment to thank them for?" },
    { emoji: '🌱', text: "How has having them in your life changed you, even in quiet ways?" },
    { emoji: '💌', text: "If you could tell them one thing and know they truly felt it, what would it be?" },
  ],

  OTHER: [
    { emoji: '💛', text: "What's your favorite memory with this person?" },
    { emoji: '😂', text: "What's the funniest moment you've shared?" },
    { emoji: '✨', text: "What do you admire most about them?" },
    { emoji: '🗣️', text: "What's the best advice they've ever given you?" },
    { emoji: '💌', text: "What would you want them to know that you've never said?" },
    { emoji: '🤗', text: "Describe a time they showed up for you when it really mattered." },
    { emoji: '🌟', text: "What makes them one of a kind?" },
    { emoji: '👶', text: "What's your earliest memory of them?" },
    { emoji: '🗺️', text: "Describe a trip, adventure, or experience you shared." },
    { emoji: '🏆', text: "What are you most proud of them for?" },
    { emoji: '🤝', text: "How has knowing them changed your life?" },
    { emoji: '🎁', text: "What's a gift — material or not — they've given you that you'll never forget?" },
    { emoji: '🌱', text: "What life lesson have you learned from them?" },
    { emoji: '🔮', text: "What do you wish for them going forward?" },
    { emoji: '🏡', text: "What does their love look like in everyday moments?" },
  ],
};

// Deadline calculations
// PRODUCTION_DAYS controls contributionDeadline (visible to contributors on the
// public /contribute/ page, NOT shown to the organizer per session 70 decision).
// ORDER_BUFFER_DAYS controls the organizer's recommended "have your book done by"
// date. Lulu's real chain: ~7 days print + ~7 days ground ship = 14 days.
export const PRODUCTION_DAYS = 21; // contributor-facing deadline (kept conservative)
export const ORDER_BUFFER_DAYS = 14; // organizer order-by recommendation

export function calculateDeadlines(deliveryDate) {
  const delivery = new Date(deliveryDate);
  const contributionDeadline = new Date(delivery);
  contributionDeadline.setDate(delivery.getDate() - PRODUCTION_DAYS);
  const orderByDate = new Date(delivery);
  orderByDate.setDate(delivery.getDate() - ORDER_BUFFER_DAYS);
  return {
    contributionDeadline: contributionDeadline.toISOString().split('T')[0],
    orderByDate: orderByDate.toISOString().split('T')[0],
  };
}

// Generate a new celebration book object
export function createCelebrationBookData({ occasion, recipientName, deliveryDate, organizerName }) {
  const occasionType = OCCASION_TYPES.find(o => o.id === occasion) || OCCASION_TYPES[5];
  const deadlines = calculateDeadlines(deliveryDate);
  return {
    id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Date.now().toString(36),
    occasion,
    recipientName,
    bookTitle: occasionType.defaultTitle(recipientName),
    dedication: '',
    subtitle: occasion === 'MOTHERS_DAY' ? "Mother's Day" : '',
    author: organizerName || '',
    coverTemplate: occasion,
    deliveryDate,
    contributionDeadline: deadlines.contributionDeadline,
    orderByDate: deadlines.orderByDate,
    status: 'COLLECTING',
    contributors: [],
    pages: [],
    coverPhotoPath: null,
    createdAt: new Date().toISOString(),
  };
}
